import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import SVG from 'react-inlinesvg';
import IconSearch from '../../../../assets/images/reskin-images/icon--search.svg';
import IconX from '../../../../assets/images/reskin-images/icon--x.svg';
import { adminAPI } from '../api';

const emailTokenRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/i;

export function parseEmailsFromText(text) {
  if (!text) return [];
  const seen = new Set();
  const out = [];
  text
    .split(/[\n\r;,\s]+/)
    .map((s) => s.trim().replace(/[\u200B-\u200D\uFEFF]/g, ''))
    .filter((s) => s.length > 0)
    .forEach((s) => {
      const lower = s.toLowerCase();
      if (emailTokenRegex.test(lower) && !seen.has(lower)) {
        seen.add(lower);
        out.push(lower);
      }
    });
  return out;
}

/**
 * Cohort roster email filter: tags, paste, cohort-scoped suggestions (same filter as table minus emails).
 */
const EmailTagSearchInput = ({
  cohortId,
  suggestionFilter,
  emailTags,
  onEmailTagsChange,
  className,
  active = true,
  placeholder = 'Search by name or email — paste a list or type',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const suggestSeq = useRef(0);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (!cohortId || !q || q.length <= 2) {
        setSuggestions([]);
        return;
      }
      const seq = ++suggestSeq.current;
      try {
        const data = await adminAPI.suggestCohortEmails({
          cohortId,
          query: q,
          filter: suggestionFilter || {},
        });
        if (seq !== suggestSeq.current) return;
        setSuggestions(data.users || []);
      } catch {
        if (seq !== suggestSeq.current) return;
        setSuggestions([]);
      }
    },
    [cohortId, suggestionFilter]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      fetchSuggestions(inputValue.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, fetchSuggestions]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  const commitTags = useCallback(
    (newOnes) => {
      if (!newOnes.length) return;
      const merged = [...emailTags];
      newOnes.forEach((e) => {
        if (!merged.includes(e)) merged.push(e);
      });
      onEmailTagsChange(merged);
    },
    [emailTags, onEmailTagsChange]
  );

  const onChangeInput = (e) => {
    const { value } = e.target;
    const parsed = parseEmailsFromText(value);
    if (parsed.length > 1 || (parsed.length === 1 && value.match(/[\n\r;,]/))) {
      commitTags(parsed);
      setInputValue('');
      setSuggestions([]);
      return;
    }
    setInputValue(value);
  };

  const onPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!pasted) return;
    const parsed = parseEmailsFromText(pasted);
    if (parsed.length) {
      e.preventDefault();
      commitTags(parsed);
      setInputValue('');
      setSuggestions([]);
    }
  };

  const onKeyDown = (e) => {
    const trimmed = inputValue.trim();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!suggestions.length) return;
      setHighlightedIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!suggestions.length) return;
      setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if ((e.key === 'Enter' || e.key === 'Tab') && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
      e.preventDefault();
      const suggestion = suggestions[highlightedIndex];
      const tagValue = suggestion.name ? suggestion.name : suggestion.email;
      commitTags([tagValue.toLowerCase()]);
      setInputValue('');
      setSuggestions([]);
      setHighlightedIndex(-1);
    } else if (e.key === 'Enter' && trimmed) {
      e.preventDefault();
      const lower = trimmed.toLowerCase();
      commitTags([lower]);
      setInputValue('');
      setSuggestions([]);
    } else if ((e.key === 'Backspace' || e.key === 'Delete') && inputValue === '' && emailTags.length > 0) {
      e.preventDefault();
      onEmailTagsChange(emailTags.slice(0, -1));
    }
  };

  const onBlurInput = () => {
    setIsFocused(false);
    const trimmed = inputValue.trim();
    if (trimmed) {
      commitTags([trimmed.toLowerCase()]);
      setInputValue('');
      setSuggestions([]);
    }
  };

  const removeTag = (email) => {
    onEmailTagsChange(emailTags.filter((t) => t !== email));
  };

  const clearAll = () => {
    onEmailTagsChange([]);
    setInputValue('');
    setSuggestions([]);
  };

  const hasContent = emailTags.length > 0 || inputValue !== '';

  return (
    <div
      className={classNames(
        'px-4 py-3 flex flex-wrap items-center gap-2 bg-white-faint rounded-2lg border-2 relative',
        className,
        {
          'border-purple bg-white': active || isFocused,
          'bg-white-faint': !active && !isFocused,
        }
      )}
    >
      <SVG
        src={IconSearch}
        className={classNames('mr-1 flex-shrink-0 search-input-icon', {
          'search-input-icon--active': active || isFocused,
        })}
      />
      {emailTags.map((email) => (
        <span
          key={email}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-100 text-sm"
        >
          {email}
          <button type="button" className="p-0 leading-none" onClick={() => removeTag(email)} aria-label={`Remove ${email}`}>
            <SVG src={IconX} style={{ width: '10px', height: '10px', display: 'inline-block', verticalAlign: 'middle' }} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className={classNames(
          'flex-1 min-w-[120px] focus:outline-none email-tag-search-input',
          { 'bg-white-faint': !active && !isFocused, 'bg-white': active || isFocused }
        )}
        type="text"
        value={inputValue}
        onChange={onChangeInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={onBlurInput}
        placeholder={emailTags.length === 0 ? placeholder : ''}
      />
      {hasContent && (
        <button type="button" className="ml-auto flex-shrink-0 p-1" onClick={clearAll} aria-label="Clear all">
          <SVG src={IconX} />
        </button>
      )}
      {suggestions.length > 0 && inputValue.trim().length > 2 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            zIndex: 50,
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            maxHeight: '240px',
            overflowY: 'auto'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.email}
              role="option"
              className={classNames('px-4 py-2 cursor-pointer', {
                'bg-purple-500 text-white': index === highlightedIndex,
                'hover:bg-purple-200': index !== highlightedIndex,
              })}
              onMouseDown={(ev) => {
                ev.preventDefault();
                const tagValue = suggestion.name ? suggestion.name : suggestion.email;
                commitTags([tagValue.toLowerCase()]);
                setInputValue('');
                setSuggestions([]);
              }}
            >
              {suggestion.name ? `${suggestion.name} (${suggestion.email})` : suggestion.email}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailTagSearchInput;
